import type { PortalBackgroundSpec } from "@axl/membership-sdk";
import { CSSProperties } from "react";

import { usePortalDesign } from "@app/shared/lib/design/portal-design";
import { useFileProvider } from "@app/shared/lib/hooks";
import { useTheme } from "@app/shared/providers/ThemeProvider";

import styles from "./PageBackdrop.module.scss";

/** The background that applies on a page: the lesson's own (`lesson.background`) unless it inherits the pages'. */
export function usePageBackground(where: "pages" | "lesson"): PortalBackgroundSpec {
  const design = usePortalDesign();
  if (where === "lesson" && design.lesson.background.type !== "inherit") return design.lesson.background;
  return design.background;
}

/**
 * A school page background (design `background` / `lesson.background`): a colour, gradient or photo fixed behind
 * the page, under a veil of the page colour (`overlay`) so text stays readable. In the dark theme the veil is at
 * least 75%: a light brand colour must not light up the dark app. `default` renders nothing. The page itself must
 * be transparent (and isolate its stacking context) for it to show.
 */
export const PageBackdrop: React.FC<{ bg: PortalBackgroundSpec }> = ({ bg }) => {
  const fileProvider = useFileProvider();
  const { resolved } = useTheme();
  if (bg.type === "default" || bg.type === "inherit") return null;

  const style: CSSProperties = {};
  if (bg.type === "color" && bg.color) style.background = bg.color;
  if (bg.type === "gradient")
    style.background = `linear-gradient(${bg.gradientAngle}deg, ${bg.gradientFrom ?? "var(--axl-primary)"}, ${
      bg.gradientTo ?? "var(--axl-primary-active)"
    })`;
  if (bg.type === "image") {
    const url = bg.imageCloudKey ? fileProvider.getUrlByKey(bg.imageCloudKey) : undefined;
    if (!url) return null;
    style.backgroundImage = `url("${url.replace(/"/g, "%22")}")`;
  }
  const veil = resolved === "dark" ? Math.max(bg.overlay, 75) : bg.overlay;

  return (
    <div className={styles.backdrop} style={style} aria-hidden>
      {veil > 0 && <div className={styles.veil} style={{ opacity: veil / 100 }} />}
    </div>
  );
};

/** Whether `bg` paints anything (the page then drops its own background colour). */
export const hasPageBackdrop = (bg: PortalBackgroundSpec) => bg.type !== "default" && bg.type !== "inherit";
